// A simulated EEG workstation screen for the sandbox.
//
// The real product streams the workstation's actual screen. Here we paint a
// generic EEG review window onto a canvas and capture it as a MediaStream, so
// the real live-view components receive real video over a real (loopback)
// peer connection. The window is deliberately generic and labelled as a demo:
// it does not imitate any vendor's software.

const W = 1280;
const H = 720;
const CHANNELS = [
  "Fp1-F7", "F7-T3", "T3-T5", "T5-O1", "Fp2-F8", "F8-T4", "T4-T6", "T6-O2",
  "Fp1-F3", "F3-C3", "C3-P3", "P3-O1", "Fp2-F4", "F4-C4", "C4-P4", "P4-O2",
];
const PX_PER_S = 90;
const GUTTER = 96;
const TOP = 86;
const BOTTOM = 34;

interface Note {
  at: number; // seconds on the sim clock
  text: string;
}

export interface InputMessage {
  type: string;
  x?: number;
  y?: number;
  button?: string;
  key?: string;
  modifiers?: string[];
}

function sample(ch: number, t: number, seed: number) {
  return (
    Math.sin(t * (9.3 + ch * 0.7) + ch + seed) * 0.5 +
    Math.sin(t * (21 + ch * 1.3) + ch * 2.1 + seed) * 0.28 +
    Math.sin(t * (3.1 + ch * 0.2) + ch * 4 + seed) * 0.55 +
    Math.sin(t * 47.7 + ch * 9 + seed) * 0.12
  ) / 1.45;
}

export class FakeWorkstation {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private timer: ReturnType<typeof setInterval> | null = null;
  private track: (MediaStreamTrack & { requestFrame?: () => void }) | null = null;
  private startedAt = performance.now();
  private cursor = { x: 0.5, y: 0.5, visible: false };
  private clicks: { x: number; y: number; at: number }[] = [];
  private typed = "";
  private notes: Note[] = [];
  private seed: number;
  /** Name shown in the on-screen badge while a live view is open. */
  badgeName: string | null = null;

  constructor(
    private patient: string,
    private systemName: string,
    notes: string[] = [],
  ) {
    this.canvas = document.createElement("canvas");
    this.canvas.width = W;
    this.canvas.height = H;
    this.ctx = this.canvas.getContext("2d")!;
    // Stable per-system waveform so two systems don't look identical.
    this.seed = Array.from(systemName).reduce((a, c) => a + c.charCodeAt(0), 0);
    notes.forEach((text, i) => this.notes.push({ at: -6 - i * 9, text }));
  }

  private now() {
    return (performance.now() - this.startedAt) / 1000;
  }

  /** Begin painting and return a stream of the screen. */
  start(fps = 15): MediaStream {
    // Safari only reliably captures frames from a canvas that is part of the
    // document and actually painted, so keep it in the page: one pixel in a
    // corner, nearly transparent. The capture uses the full 1280x720 bitmap
    // regardless of the displayed size.
    this.canvas.style.cssText = "position:fixed;left:0;bottom:0;width:1px;height:1px;opacity:0.02;pointer-events:none;z-index:0;";
    this.canvas.setAttribute("aria-hidden", "true");
    document.body.appendChild(this.canvas);
    const stream = (this.canvas as any).captureStream(fps) as MediaStream;
    this.track = stream.getVideoTracks()[0] || null;
    this.draw();
    // setInterval, not requestAnimationFrame: the stream must keep producing
    // frames in a background tab or the viewer's stall watchdog trips.
    this.timer = setInterval(() => this.draw(), Math.round(1000 / fps));
    return stream;
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.track = null;
    if (this.canvas.parentNode) this.canvas.parentNode.removeChild(this.canvas);
  }

  addNote(text: string) {
    this.notes.push({ at: this.now(), text });
  }

  handleInput(msg: InputMessage) {
    switch (msg.type) {
      case "MOUSE_MOVE":
        if (typeof msg.x === "number" && typeof msg.y === "number") {
          this.cursor = { x: msg.x, y: msg.y, visible: true };
        }
        break;
      case "MOUSE_DOWN":
        this.clicks.push({ x: this.cursor.x, y: this.cursor.y, at: this.now() });
        break;
      case "KEY_EVENT": {
        const k = msg.key || "";
        if (k === "backspace") this.typed = this.typed.slice(0, -1);
        else if (k === "enter") this.typed = "";
        else if (k === "space") this.typed += " ";
        else if (k.length === 1) this.typed += k;
        this.typed = this.typed.slice(-60);
        break;
      }
    }
  }

  /** One still frame as a JPEG data URI, as a system check captures it. */
  still(): string {
    const badge = this.badgeName;
    this.badgeName = null;
    this.draw();
    this.badgeName = badge;
    return this.canvas.toDataURL("image/jpeg", 0.85);
  }

  private draw() {
    const c = this.ctx;
    const t = this.now();

    // Window chrome
    c.fillStyle = "#ffffff";
    c.fillRect(0, 0, W, H);
    c.fillStyle = "#1e3a5f";
    c.fillRect(0, 0, W, 34);
    c.fillStyle = "#ffffff";
    c.font = "600 15px 'Segoe UI', system-ui, sans-serif";
    c.textBaseline = "middle";
    const clock = new Date().toLocaleTimeString("en-US");
    c.fillText(
      `EEG Review (sandbox demo)   ·   Live: ${this.patient || "No study"}   ·   Time of day: ${clock}`,
      14,
      17,
    );
    c.textAlign = "right";
    c.fillText(this.systemName, W - 14, 17);
    c.textAlign = "left";

    // Toolbar
    c.fillStyle = "#eef2f7";
    c.fillRect(0, 34, W, 44);
    c.strokeStyle = "#d5dce6";
    c.strokeRect(0.5, 34.5, W - 1, 44);
    const tools = ["Montage: Double banana", "Sens 7 µV/mm", "30 mm/s", "HFF 70 Hz", "LFF 1 Hz", "Note"];
    let tx = 12;
    c.font = "500 13px 'Segoe UI', system-ui, sans-serif";
    for (const label of tools) {
      const w = c.measureText(label).width + 22;
      c.fillStyle = "#ffffff";
      c.fillRect(tx, 42, w, 28);
      c.strokeStyle = "#c3cddb";
      c.strokeRect(tx + 0.5, 42.5, w, 28);
      c.fillStyle = "#1e293b";
      c.fillText(label, tx + 11, 56);
      tx += w + 8;
    }

    // Trace area
    const areaH = H - TOP - BOTTOM;
    const rh = areaH / CHANNELS.length;
    c.strokeStyle = "#e8edf3";
    c.lineWidth = 1;
    c.beginPath();
    const off = (t * PX_PER_S) % PX_PER_S;
    for (let gx = W - off; gx > GUTTER; gx -= PX_PER_S) {
      c.moveTo(gx, TOP);
      c.lineTo(gx, H - BOTTOM);
    }
    c.stroke();

    c.font = "500 12px Consolas, 'SF Mono', monospace";
    const live = !!this.patient;
    for (let ch = 0; ch < CHANNELS.length; ch++) {
      const y0 = TOP + rh * (ch + 0.5);
      c.fillStyle = "#475569";
      c.fillText(CHANNELS[ch], 12, y0);
      c.strokeStyle = "#0f172a";
      c.lineWidth = 1.1;
      c.beginPath();
      for (let x = GUTTER; x <= W; x += 2) {
        const ts = t - (W - x) / PX_PER_S;
        const y = y0 + (live ? sample(ch, ts, this.seed) * rh * 0.38 : 0);
        if (x === GUTTER) c.moveTo(x, y);
        else c.lineTo(x, y);
      }
      c.stroke();
    }

    // Notes scroll with the trace
    c.font = "600 12px 'Segoe UI', system-ui, sans-serif";
    for (const n of this.notes) {
      const x = W - (t - n.at) * PX_PER_S;
      if (x < GUTTER || x > W) continue;
      c.strokeStyle = "#dc2626";
      c.lineWidth = 1.5;
      c.beginPath();
      c.moveTo(x, TOP);
      c.lineTo(x, H - BOTTOM);
      c.stroke();
      const label = n.text.slice(0, 48);
      const w = c.measureText(label).width + 12;
      const lx = Math.min(x + 4, W - w - 4);
      c.fillStyle = "#fee2e2";
      c.fillRect(lx, TOP + 4, w, 20);
      c.fillStyle = "#991b1b";
      c.fillText(label, lx + 6, TOP + 14);
    }

    // Status bar
    c.fillStyle = "#eef2f7";
    c.fillRect(0, H - BOTTOM, W, BOTTOM);
    c.fillStyle = "#334155";
    c.font = "500 13px 'Segoe UI', system-ui, sans-serif";
    c.fillText(live ? "● Recording" : "No study open", 14, H - BOTTOM / 2);
    if (this.typed) c.fillText(`Keyboard: ${this.typed}`, 180, H - BOTTOM / 2);

    // Click ripples and the remote cursor
    this.clicks = this.clicks.filter((k) => t - k.at < 0.6);
    for (const k of this.clicks) {
      const p = (t - k.at) / 0.6;
      c.strokeStyle = `rgba(37, 99, 235, ${1 - p})`;
      c.lineWidth = 2;
      c.beginPath();
      c.arc(k.x * W, k.y * H, 6 + p * 22, 0, Math.PI * 2);
      c.stroke();
    }
    if (this.cursor.visible) {
      const x = this.cursor.x * W;
      const y = this.cursor.y * H;
      c.fillStyle = "#0f172a";
      c.strokeStyle = "#ffffff";
      c.lineWidth = 1.5;
      c.beginPath();
      c.moveTo(x, y);
      c.lineTo(x, y + 18);
      c.lineTo(x + 5, y + 13.5);
      c.lineTo(x + 9, y + 21);
      c.lineTo(x + 12, y + 19.5);
      c.lineTo(x + 8, y + 12.5);
      c.lineTo(x + 14, y + 12.5);
      c.closePath();
      c.fill();
      c.stroke();
    }

    // The workstation's "Live View Active" badge, bottom-right.
    if (this.badgeName) {
      const bw = 210;
      const bh = 52;
      const bx = W - bw - 18;
      const by = H - BOTTOM - bh - 14;
      c.fillStyle = "rgba(17, 24, 39, 0.82)";
      c.beginPath();
      (c as any).roundRect ? (c as any).roundRect(bx, by, bw, bh, 12) : c.rect(bx, by, bw, bh);
      c.fill();
      const pulse = 0.5 + 0.5 * Math.sin(t * 2.6);
      c.fillStyle = `rgba(59, 130, 246, ${0.25 + 0.25 * pulse})`;
      c.beginPath();
      c.arc(bx + 24, by + bh / 2, 9 + pulse * 3, 0, Math.PI * 2);
      c.fill();
      c.fillStyle = "#3b82f6";
      c.beginPath();
      c.arc(bx + 24, by + bh / 2, 5, 0, Math.PI * 2);
      c.fill();
      c.fillStyle = "#f8fafc";
      c.font = "600 13px 'Segoe UI', system-ui, sans-serif";
      c.fillText("Live View Active", bx + 44, by + 19);
      c.fillStyle = "rgba(248, 250, 252, 0.7)";
      c.font = "400 12px 'Segoe UI', system-ui, sans-serif";
      c.fillText(this.badgeName, bx + 44, by + 36);
    }

    // Hand the finished frame to the capture track explicitly. Chrome and
    // Safari both support this; it avoids relying on paint detection.
    if (this.track && typeof this.track.requestFrame === "function") {
      try { this.track.requestFrame(); } catch (_) {}
    }
  }
}
